import { browser } from "$app/environment";

import { type Readable, writable } from "svelte/store";

/** The clock the dashboard renders times on, as an Intl hour cycle. */
export const TIME_FORMATS = ["h23", "h12"] as const;
export type TimeFormat = (typeof TIME_FORMATS)[number];

/**
 * Persisted in a cookie so the clock is known before `/api/user` resolves;
 * reading it from the account alone would render every timestamp 24-hour and
 * flip them once the fetch lands.
 */
const TIME_FORMAT_COOKIE_NAME = "modal-time-format";

const TIME_FORMAT_COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60; // browser cap

/** The clock applied when the user has not chosen one. */
export const DEFAULT_TIME_FORMAT: TimeFormat = "h23";

export function isTimeFormat(value: unknown): value is TimeFormat {
  return TIME_FORMATS.includes(value as TimeFormat);
}

function readPreferenceCookie(): TimeFormat {
  if (!browser) return DEFAULT_TIME_FORMAT;
  const value = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${TIME_FORMAT_COOKIE_NAME}=`))
    ?.split("=")[1];
  return isTimeFormat(value) ? value : DEFAULT_TIME_FORMAT;
}

function writePreferenceCookie(value: TimeFormat) {
  if (!browser) return;
  const secure = location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${TIME_FORMAT_COOKIE_NAME}=${value}; path=/; max-age=${TIME_FORMAT_COOKIE_MAX_AGE_SECONDS}; samesite=lax${secure}`;
}

const storedPreference = writable<TimeFormat>(readPreferenceCookie());

/** A readable time format that can also be set (but not `update`d). */
export type TimeFormatStore = Readable<TimeFormat> & {
  set: (value: TimeFormat) => void;
};

/** The persisted time format, falling back to `DEFAULT_TIME_FORMAT`. */
export const timeFormatPreference: TimeFormatStore = {
  subscribe: storedPreference.subscribe,
  set: (value: TimeFormat) => {
    writePreferenceCookie(value);
    storedPreference.set(value);
  },
};
